import type { FetchMatcher, JsonApiResource } from 'tio-data/demo/types';
import type {
  AccountStatementStructuredContent,
  NsldsStructuredContent,
} from 'tio-data/data/resources/observable-document.type';
import {
  getDemoDebtLevel,
  type DemoDebtLevel,
  type DemoDocumentStatusId,
  type DemoSessionSelectionSnapshot,
} from 'tio-employee/utils/career-pathways-demo-data';
import { PERSON_ID, resolveDemoFixtureIdentity } from 'tio-employee/demo/identity-fixtures';
import {
  DEMO_LOAN_ACCOUNT_NUMBER,
  DEMO_LOAN_NAME,
  DEMO_LOAN_ORIGINAL_DATE,
  DEMO_LOAN_SERVICER,
  DEMO_PSLF_LOAN_PLAN,
  DEMO_PSLF_QUALIFYING_PAYMENTS,
  demoLoanBalanceSplit,
  demoLoanPlan,
  demoPslfActive,
} from 'tio-employee/demo/loan-profile';
import { buildStatementPdf } from 'tio-employee/demo/statement-pdf';
import {
  demoUploadedDocument,
  demoUploadedDocuments,
  discardDemoDocument,
  isDemoDocumentDiscarded,
  type DemoUploadReadBack,
} from 'tio-employee/demo/upload-fixtures';

interface DocumentStates {
  extractionState: string;
  reflectionState: string;
  subsistenceState: string;
}

const PROCESSED = 'ExtractionState.PROCESSED';
const NOT_APPLIED = 'ReflectionState.NOT_APPLIED';
const IMPLICIT = 'SubsistenceState.IMPLICIT';

// Where each outcome leaves tio-api ObservableDocument's three state machines (validation_failed, process_failed, reflection_failed…).
const STATES_BY_STATUS: Record<DemoDocumentStatusId, DocumentStates> = {
  applied: {
    extractionState: PROCESSED,
    reflectionState: 'ReflectionState.APPLIED',
    subsistenceState: IMPLICIT,
  },
  'partially-applied': {
    extractionState: PROCESSED,
    reflectionState: 'ReflectionState.PARTIALLY_APPLIED',
    subsistenceState: IMPLICIT,
  },
  'not-applied': {
    extractionState: PROCESSED,
    reflectionState: NOT_APPLIED,
    subsistenceState: IMPLICIT,
  },
  processing: {
    extractionState: 'ExtractionState.PROCESSING',
    reflectionState: NOT_APPLIED,
    subsistenceState: IMPLICIT,
  },
  invalid: {
    extractionState: 'ExtractionState.INVALID',
    reflectionState: NOT_APPLIED,
    subsistenceState: IMPLICIT,
  },
  failed: {
    extractionState: 'ExtractionState.FAILED',
    reflectionState: NOT_APPLIED,
    subsistenceState: IMPLICIT,
  },
};

// The NSLDS file was processed and applied before the demo, whatever the picked status.
const APPLIED_STATES = STATES_BY_STATUS.applied;

// Outcomes where reflection placed the statement's account and loans on the person.
const REFLECTED_STATUSES = new Set<DemoDocumentStatusId>(['applied', 'partially-applied']);

const STATEMENT_DATE = { year: '2026', month: '04', day: '01' };

interface NsldsPlan {
  code: string;
  description: string;
}

const NSLDS_STANDARD_PLAN: NsldsPlan = { code: 'SF', description: 'STANDARD REPAYMENT' };

// NSLDS plan codes as tio-api's processed NSLDS seeds carry them, by loan-layer plan key.
const NSLDS_PLAN_BY_KEY: Record<string, NsldsPlan | undefined> = {
  tenYearsStandard: NSLDS_STANDARD_PLAN,
  [DEMO_PSLF_LOAN_PLAN.planKey]: { code: 'IB', description: 'INCOME-BASED REPAYMENT' },
};

type ExtractedContent = AccountStatementStructuredContent | NsldsStructuredContent;

type NsldsLoan = NsldsStructuredContent['loans'][number] & {
  loan_repayment_plan_type_code: string;
  loan_repayment_plan_type_code_description: string;
  loan_pslf_cumulative_matched_months?: string;
};

interface DemoDocument {
  id: string;
  filename: string;
  via: string;
  createdAt: string;
  provider: string;
}

interface LinkedDocument extends DemoDocument {
  contentType: string;
}

export const DEMO_DOCUMENTS: LinkedDocument[] = [
  // studentaid.gov's aid data download, a .txt file tio-api classifies as NSLDS by its content type.
  {
    id: 'demo-loan-doc-1',
    filename: 'MyStudentData.txt',
    contentType: 'text/plain',
    via: 'EMAIL',
    createdAt: '2026-05-15T09:12:00Z',
    provider: 'ObservableProvider.NSLDS',
  },
  {
    id: 'demo-loan-doc-2',
    filename: `mohela-statement-${STATEMENT_DATE.year}-${STATEMENT_DATE.month}.pdf`,
    contentType: 'application/pdf',
    via: 'UPLOAD',
    createdAt: '2026-04-02T14:30:00Z',
    provider: 'ObservableProvider.ACCOUNT_STATEMENT',
  },
];

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const LINKED_FILE_URLS = new Map<string, string>();

// Browsers block a new tab's navigation to a data: URL, so Download opens an object URL, one per distinct file.
function linkedFileUrl(contents: string, type: string): string {
  const key = `${type}\n${contents}`;
  const cached = LINKED_FILE_URLS.get(key);
  if (cached) {
    return cached;
  }
  const url = URL.createObjectURL(new Blob([contents], { type }));
  LINKED_FILE_URLS.set(key, url);
  return url;
}

/** The linked statement's PDF for a debt level, as ASCII bytes. */
export function demoStatementPdf(debt: DemoDebtLevel): string {
  const balanceDollars = debt.balanceDollars ?? 0;
  const { principalDollars, interestDollars } = demoLoanBalanceSplit(balanceDollars);
  const { year, month, day } = STATEMENT_DATE;
  return buildStatementPdf(DEMO_LOAN_SERVICER, [
    'Student Loan Statement',
    `Account Number: ${DEMO_LOAN_ACCOUNT_NUMBER}`,
    `Statement Date: ${month}/${day}/${year}`,
    `Loan: ${DEMO_LOAN_NAME}`,
    `Principal Balance: ${usd.format(principalDollars)}`,
    `Interest Balance: ${usd.format(interestDollars)}`,
    `Current Balance: ${usd.format(balanceDollars)}`,
  ]);
}

/** The linked statement's PDF as its asset's signedUrl: an object URL, the same one for the same debt level. */
export function demoStatementPdfUrl(debt: DemoDebtLevel): string {
  return linkedFileUrl(demoStatementPdf(debt), 'application/pdf');
}

const nsldsLabel = (key: string) =>
  key
    .split('_')
    .map((word) => (word === 'pslf' ? 'PSLF' : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');

const nsldsLines = (fields: object) =>
  Object.entries(fields).map(([key, value]) => `${nsldsLabel(key)}:${String(value ?? '')}`);

/** The linked NSLDS file's text: one `Label:value` line per extracted field. */
export function demoNsldsFile(content: NsldsStructuredContent): string {
  return [
    ...nsldsLines(content.file),
    ...nsldsLines(content.student),
    ...content.loans.flatMap(nsldsLines),
  ].join('\n');
}

/** The linked NSLDS file as its asset's signedUrl: an object URL, the same one for the same content. */
export function demoNsldsFileUrl(content: NsldsStructuredContent): string {
  return linkedFileUrl(demoNsldsFile(content), 'text/plain');
}

function statementContent(
  doc: DemoDocument,
  debt: DemoDebtLevel
): AccountStatementStructuredContent {
  const { principalDollars } = demoLoanBalanceSplit(debt.balanceDollars ?? 0);
  return {
    filename: doc.filename,
    servicer_name: DEMO_LOAN_SERVICER,
    account_number: DEMO_LOAN_ACCOUNT_NUMBER,
    principal_balance_amount: principalDollars * 100,
    loan_details: {
      [DEMO_LOAN_NAME]: {
        loan_name: DEMO_LOAN_NAME,
        loan_type: DEMO_LOAN_NAME,
        loan_current_principal_balance: principalDollars.toFixed(2),
        loan_disbursement_date: DEMO_LOAN_ORIGINAL_DATE,
      },
    },
  };
}

function nsldsContent(
  doc: DemoDocument,
  debt: DemoDebtLevel,
  selection: DemoSessionSelectionSnapshot
): NsldsStructuredContent {
  const { principalDollars, interestDollars } = demoLoanBalanceSplit(debt.balanceDollars ?? 0);
  const borrower = resolveDemoFixtureIdentity(selection);
  const pslf = demoPslfActive(selection);
  const plan =
    NSLDS_PLAN_BY_KEY[demoLoanPlan(debt.repaymentPlan, pslf).planKey] ?? NSLDS_STANDARD_PLAN;
  const loan: NsldsLoan = {
    loan_type_code: 'D5',
    loan_type_description: DEMO_LOAN_NAME.toUpperCase(),
    loan_outstanding_principal_balance: principalDollars.toFixed(2),
    loan_outstanding_interest_balance: interestDollars.toFixed(2),
    loan_date: DEMO_LOAN_ORIGINAL_DATE,
    current_loan_status_description: 'IN REPAYMENT',
    loan_repayment_plan_type_code: plan.code,
    loan_repayment_plan_type_code_description: plan.description,
    // Only PSLF sessions carry a count, matching the loan graph's forgiveness-program-qualified.
    ...(pslf ? { loan_pslf_cumulative_matched_months: String(DEMO_PSLF_QUALIFYING_PAYMENTS) } : {}),
  };
  return {
    filename: doc.filename,
    file: { file_request_date: doc.createdAt },
    student: {
      student_first_name: borrower.firstName.toUpperCase(),
      student_middle_initial: '',
      student_last_name: borrower.lastName.toUpperCase(),
      student_total_all_grants: '0.00',
      student_total_all_loans_outstanding_principal: principalDollars.toFixed(2),
      student_total_all_loans_outstanding_interest: interestDollars.toFixed(2),
    },
    loans: [loan],
  };
}

function assetResource(
  doc: LinkedDocument,
  debt: DemoDebtLevel,
  selection: DemoSessionSelectionSnapshot
): JsonApiResource {
  return {
    type: 'observable-asset',
    id: `${doc.id}-asset`,
    attributes: {
      filename: doc.filename,
      contentType: doc.contentType,
      signedUrl:
        doc.provider === 'ObservableProvider.NSLDS'
          ? demoNsldsFileUrl(nsldsContent(doc, debt, selection))
          : demoStatementPdfUrl(debt),
    },
    relationships: {
      observableDocument: { data: { type: 'observable-document', id: doc.id } },
    },
  };
}

// tio-api narrows a PDF's provider only once extraction succeeds, and serves structured content only then.
function extractedAttributes(
  statusId: DemoDocumentStatusId,
  classification: string,
  content: (provider: string) => ExtractedContent
) {
  const states = STATES_BY_STATUS[statusId] ?? APPLIED_STATES;
  if (states.extractionState !== PROCESSED) {
    return { ...states, provider: classification, structuredContent: null };
  }
  const provider =
    classification === 'ObservableProvider.NSLDS'
      ? classification
      : 'ObservableProvider.ACCOUNT_STATEMENT';
  return { ...states, provider, structuredContent: content(provider) };
}

function documentContent(
  doc: DemoDocument,
  debt: DemoDebtLevel,
  selection: DemoSessionSelectionSnapshot
): ExtractedContent {
  return doc.provider === 'ObservableProvider.NSLDS'
    ? nsldsContent(doc, debt, selection)
    : statementContent(doc, debt);
}

function documentResource(
  doc: DemoDocument,
  debt: DemoDebtLevel,
  selection: DemoSessionSelectionSnapshot
): JsonApiResource {
  const outcome =
    doc.provider === 'ObservableProvider.NSLDS'
      ? {
          ...APPLIED_STATES,
          provider: doc.provider,
          structuredContent: documentContent(doc, debt, selection),
        }
      : extractedAttributes(
          selection.documentStatusId,
          'ObservableProvider.AMBIGUOUS',
          (provider) => documentContent({ ...doc, provider }, debt, selection)
        );
  return {
    type: 'observable-document',
    id: doc.id,
    attributes: {
      via: doc.via,
      createdAt: doc.createdAt,
      ...outcome,
    },
    relationships: {
      observableAsset: { data: { type: 'observable-asset', id: `${doc.id}-asset` } },
      person: { data: { type: 'person', id: PERSON_ID } },
    },
  };
}

const searchParams = (url: string) => new URL(url, 'http://demo.invalid').searchParams;

function pageOf<T>(items: T[], url: string): T[] {
  const params = searchParams(url);
  const offset = Number(params.get('page[offset]')) || 0;
  const limit = Number(params.get('page[limit]')) || items.length;
  return items.slice(offset, offset + limit);
}

// tio-api ObservableDocumentResource's discarded_at filter reads nil, none, null and 0 as "not discarded".
const excludesDiscarded = (url: string) =>
  /^(nil|none|null|0)$/i.test(searchParams(url).get('filter[discarded_at]') ?? '');

const documentIdIn = (url: string) => /\/observable-documents\/([^/?]+)(\?|$)/.exec(url)?.[1];

function uploadReadAs(selection: DemoSessionSelectionSnapshot): DemoUploadReadBack {
  const debt = getDemoDebtLevel(selection.debtLevelId);
  const uploadDebt = debt.hasLinkedLoans ? debt : getDemoDebtLevel('low');
  const uploadStatusId =
    !debt.hasLinkedLoans && REFLECTED_STATUSES.has(selection.documentStatusId)
      ? 'not-applied'
      : selection.documentStatusId;
  return (upload) =>
    upload.extractionState === 'ExtractionState.DISPATCHED'
      ? extractedAttributes(uploadStatusId, upload.provider, (provider) =>
          documentContent({ ...upload, provider }, uploadDebt, selection)
        )
      : undefined;
}

/** The local batch event a demo upload sends after its brief processing view. */
export function demoUploadBatchStatus(id: string, selection: DemoSessionSelectionSnapshot) {
  const entry = demoUploadedDocument(id, uploadReadAs(selection));
  const attributes = entry?.document.attributes;
  if (!attributes || typeof attributes['batch'] !== 'string') return undefined;
  const extractionState = attributes['extractionState'];
  const reflectionState = attributes['reflectionState'];
  const event =
    extractionState === 'ExtractionState.FAILED' || extractionState === 'ExtractionState.INVALID'
      ? 'extraction_failed'
      : extractionState === 'ExtractionState.PROCESSING'
        ? 'extraction_started'
        : reflectionState === 'ReflectionState.NOT_APPLIED'
          ? 'reflection_failed'
          : 'content_reflected';
  return {
    batchId: attributes['batch'],
    message: { id, recordAttributes: attributes, transition: { event } },
  };
}

/** Fetch-seam reads and deletes of observable documents, uploads included: document page and table. */
export function buildLoanDocumentFetchMatchers(
  selection: DemoSessionSelectionSnapshot
): FetchMatcher[] {
  const debt = getDemoDebtLevel(selection.debtLevelId);
  // A persona without linked loans has no loan documents until the operator uploads one.
  const linkedDocuments = debt.hasLinkedLoans ? DEMO_DOCUMENTS : [];
  const linkedEntry = (doc: LinkedDocument) => ({
    document: documentResource(doc, debt, selection),
    asset: assetResource(doc, debt, selection),
  });
  // Creates stay in their initial state; reads show the picked status for uploads sent to extraction.
  const readAs = uploadReadAs(selection);

  const entryFor = (id: string) => {
    const linked = linkedDocuments.find((d) => d.id === id);
    return demoUploadedDocument(id, readAs) ?? (linked ? linkedEntry(linked) : undefined);
  };
  const servedId = (url: string) => {
    const id = documentIdIn(url);
    return id && entryFor(id) ? id : undefined;
  };

  return [
    // An unknown id gets data: null so a wrong-id read surfaces instead of showing another document.
    {
      urlMatches: (url: string) => documentIdIn(url) !== undefined,
      method: 'GET',
      json: (url: string) => {
        const entry = entryFor(documentIdIn(url) ?? '');
        if (!entry) {
          return { data: null };
        }
        return { data: entry.document, included: entry.asset ? [entry.asset] : [] };
      },
    },
    // The table's delete: tio-api discards the document and answers 204 with no body.
    {
      urlMatches: (url: string) => servedId(url) !== undefined,
      method: 'DELETE',
      status: 204,
      json: (url: string) => {
        const id = servedId(url);
        if (id) {
          discardDemoDocument(id);
        }
        return null;
      },
    },
    // Newest first, as the table sorts: this page load's uploads, then the linked documents.
    {
      urlMatches: (url: string) => /\/observable-documents(\?|$)/.test(url),
      method: 'GET',
      json: (url: string) => {
        const entries = [
          ...demoUploadedDocuments(readAs),
          ...linkedDocuments.map(linkedEntry),
        ].filter((entry) => !excludesDiscarded(url) || !isDemoDocumentDiscarded(entry.document.id));
        const page = pageOf(entries, url);
        return {
          data: page.map((entry) => entry.document),
          included: page.flatMap((entry) => (entry.asset ? [entry.asset] : [])),
          meta: { page: { total: entries.length } },
        };
      },
    },
  ];
}
